import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { Checkbox } from 'components/ui/Checkbox';
import { toast } from 'react-toastify';
import { getOperatingHours, updateOperatingHours, deleteSpecialOperatingHour } from 'services/businessService';
import {isValidGuid}  from 'utils/GuidValidator';

const OperatingHoursTab = ({selectedBusiness }) => {
  const [scheduleData, setScheduleData] = useState([]);

  const [specialHours, setSpecialHours] = useState([]);

  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSpecialHours, setIsLoadingSpecialHours] = useState(null);


  const handleScheduleChange = (scheduleId, field, value) => {

    setScheduleData(prev =>
      prev.map(item=> item.id === scheduleId ? { ...item, [field]: value } : item) 
    );
    setHasChanges(true);
  };

  const handleSpecialHourChange = (id, field, value) => {
    setSpecialHours(prev => prev?.map(hour => 
      hour?.id === id ? { ...hour, [field]: value } : hour
    ));
    setHasChanges(true);
  };

  const addSpecialHour = () => {
    const newSpecialHour = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      description: '',
      closed: false,
      startDateTime: '08:00',
      endDateTime: '17:00'
    };
    setSpecialHours(prev => [...prev, newSpecialHour]);
    setHasChanges(true);
  };

  const  removeSpecialHour = async(id) => {
    if(!isValidGuid(id)){
      setSpecialHours(prev => prev?.filter(hour => hour?.id !== id));
      return;  
    }
    setIsLoadingSpecialHours(id);
    const results = await deleteSpecialOperatingHour(selectedBusiness.businessId, id);
    if(!results?.success){
      toast.error(results?.message || 'Failed to delete special hour');
      return;
    }
    else{
      toast.success('Special hour deleted successfully');
      setSpecialHours(prev => prev?.filter(hour => hour?.id !== id));
    }
    setIsLoadingSpecialHours(null);
  };

  const convertDateTime = (strDateTimeList = [])=>{
    return strDateTimeList.map(strDateTime=>{
      return{
        ...strDateTime,
        id: isValidGuid(strDateTime.id) ? strDateTime.id : null,
        startDateTime: strDateTime.startDateTime.length > 11 ? new Date(strDateTime.date): new Date(strDateTime.date+'T'+strDateTime.startDateTime),
        endDateTime: strDateTime.endDateTime.length > 11 ? new Date(strDateTime.date): new Date(strDateTime.date+'T'+strDateTime.endDateTime)
      }
    });
  }

  const validateSpecialHours = (convertedSpecialHours =[])=>{
    let isValid = true;

    convertedSpecialHours.forEach(specialHour=>{
      if(!specialHour.date){
        toast.error('Please select a date for all special hours');
        isValid = false;
        return;
      }

      if(!specialHour.description || specialHour.description.trim() === ''){
        toast.error('Please enter a description for all special hours');
        isValid = false;
        return;
      }

      if(specialHour.startDateTime >= specialHour.endDateTime && !specialHour.closed){
        toast.error(`Start time must be before end time for ${specialHour.description || specialHour.date}`);
        isValid = false;
        return;
      }
    });

    return isValid;
  }
  const handleSave = async () => {
    const convertedSpecialHours = convertDateTime(specialHours);

    if(!validateSpecialHours(convertedSpecialHours)){
      return;
    }
    const payload = {
      weeklySchedules: scheduleData,
      specialBusinessHours: convertedSpecialHours
    };

    setIsLoading(true);
    const results = await updateOperatingHours(selectedBusiness.businessId, payload);
    if(!results?.success){
      toast.error(results?.message || 'Failed to update operating hours');
    }
    else{
      toast.success('Operating hours updated successfully');
      setHasChanges(false);
    }
    setIsLoading(false);
  };

  const formatDisplayTime = (time) => {
    if (!time) return '';
    
    // const sanitizedTime = time.length > 11 ? time.slice(11) : time;
    const [hours, minutes, seconds] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeOnly = (time) => {
    if (!time) return '';
    
    const sanitizedTime = time.length > 11 ? time.slice(11) : time;
    const [hours, minutes, seconds] = sanitizedTime.split(':');
    return `${hours}:${minutes}`;
  };

  const loadOperatingHours = async () => {

    const results = await getOperatingHours(selectedBusiness.businessId);
    if(results?.success){
      const {weeklySchedules,specialBusinessHours} = results.data;
      setScheduleData(weeklySchedules);
      setSpecialHours(specialBusinessHours);
    }
    else{
      toast.error(results?.message || 'Failed to load operating hours');
    }
  }

  useEffect(()=>{
    if(selectedBusiness?.businessId == null)
      return;

    loadOperatingHours();
  },[selectedBusiness?.businessId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Operating Hours</h3>
          <p className="text-sm text-muted-foreground">
            Configure your business hours and special schedules
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-2">
            <div className="status-indicator warning"></div>
            <span className="text-sm text-warning">Unsaved changes</span>
          </div>
        )}
      </div>
      {/* Regular Hours */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Weekly Schedule</h4>
        <div className="space-y-4">
          {scheduleData?.map((schedule) => (
            <div key={scheduleData?.id} className="flex items-center space-x-4 p-4 bg-muted/30 rounded-lg">
              <div className="w-24">
                <span className="text-sm font-medium text-foreground">{schedule?.day}</span>
              </div>
              
              <div className="flex items-center space-x-4 flex-1">
                <Checkbox
                  checked={schedule?.open}
                  onChange={(e) => handleScheduleChange(schedule?.id, 'open', e?.target?.checked)}
                  label="Open"
                />
                
                {schedule?.open && (
                  <>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="time"
                        value={schedule?.startTime}
                        onChange={(e) => handleScheduleChange(schedule?.id, 'startTime', e?.target?.value)}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={schedule?.endTime}
                        onChange={(e) => handleScheduleChange(schedule?.id, 'endTime', e?.target?.value)}
                        className="w-32"
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatDisplayTime(schedule?.startTime)} - {formatDisplayTime(schedule?.endTime)}
                    </div>
                  </>
                )}
                
                {!schedule.open && (
                  <span className="text-sm text-muted-foreground italic">Closed</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Special Hours */}
      <div className="bg-card border border-border rounded-lg py-6">
        <div className="flex items-center justify-between mb-4 px-6">
          <h4 className="text-md font-medium text-foreground">Special Hours & Holidays</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={addSpecialHour}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
          >
            Add Special Hours
          </Button>
        </div>

        <div className="space-y-4">
          {specialHours?.map((specialHour) => (
            <div key={specialHour?.id} className="flex items-center space-x-4 p-4 bg-muted/30 rounded-lg">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-2">
                <Input
                  type="date"
                  value={specialHour?.date}
                  onChange={(e) => handleSpecialHourChange(specialHour?.id, 'date', e?.target?.value)}
                  placeholder="Select date"
                />
                
                <Input
                  type="text"
                  value={specialHour?.description}
                  onChange={(e) => handleSpecialHourChange(specialHour?.id, 'description', e?.target?.value)}
                  placeholder="Holiday/Event name"
                />
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={specialHour?.closed}
                    onChange={(e) => handleSpecialHourChange(specialHour?.id, 'closed', e?.target?.checked)}
                    label="Closed"
                  />
                </div>
                
                {!specialHour?.closed && (
                  <div className="flex items-center space-x-2">
                    <Input
                      type="time"
                      value={formatTimeOnly(specialHour?.startDateTime)}
                      onChange={(e) => handleSpecialHourChange(specialHour?.id, 'startDateTime', e?.target?.value)}
                      className="w-32"
                    />
                    <span className="text-xs text-muted-foreground">-</span>
                    <Input
                      type="time"
                      value={formatTimeOnly(specialHour?.endDateTime)}
                      onChange={(e) => handleSpecialHourChange(specialHour?.id, 'endDateTime', e?.target?.value)}
                      className="w-32"
                    />
                  </div>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeSpecialHour(specialHour?.id)}
                className="text-destructive hover:text-destructive"
                disabled={isLoadingSpecialHours === specialHour?.id}
                loading={isLoadingSpecialHours === specialHour?.id}
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          ))}
          
          {specialHours?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Calendar" size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No special hours configured</p>
            </div>
          )}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-end pt-4 border-t border-border">
        {/* <div className="flex items-center space-x-2">
          <div className="status-indicator active"></div>
          <span className="text-sm text-muted-foreground">Auto-save enabled</span>
        </div> */}

        <Button
          variant="default"
          onClick={handleSave}
          disabled={!hasChanges}
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
          iconSize={16}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default OperatingHoursTab;